import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Shield, Lock, Eye, Database, Mail, Globe } from 'lucide-react';

export default function PrivacyPolicy() {
  const lastUpdated = "March 28, 2026";
  
  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-20 md:pb-8">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-primary mb-2 flex items-center gap-3">
            <Shield className="h-8 w-8 text-purple-600" />
            Privacy Policy
          </h1>
          <p className="text-muted-foreground">
            Last updated: {lastUpdated}
          </p>
        </div>

        <Card className="mb-6">
          <CardContent className="pt-6 prose prose-slate max-w-none">
            <p className="text-lg">
              GlobalVyapar ("we", "us", or "our") is committed to protecting your privacy. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your 
              information when you use our B2B marketplace platform.
            </p>
          </CardContent>
        </Card>

        <div className="space-y-6">
          {/* Information We Collect */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5 text-blue-600" />
                1. Information We Collect
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2">Personal Information</h4>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                  <li>Name and contact information (email, phone number)</li>
                  <li>Company name and business details</li>
                  <li>GSTIN and IEC numbers for verification</li>
                  <li>Profile photos and company logos</li>
                  <li>Payment and billing information</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Usage Information</h4>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                  <li>Products viewed and searched</li>
                  <li>Inquiries sent and received</li>
                  <li>Communication history on the platform</li>
                  <li>Device information and IP addresses</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* How We Use Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5 text-green-600" />
                2. How We Use Your Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                <li>To provide and maintain our B2B marketplace services</li>
                <li>To verify your business credentials (GSTIN, IEC)</li>
                <li>To facilitate communication between buyers and sellers</li>
                <li>To provide AI-powered features (product matching, drafts, negotiations)</li>
                <li>To send important notifications about your account and transactions</li>
                <li>To improve our platform and develop new features</li>
                <li>To comply with legal obligations</li>
              </ul>
            </CardContent>
          </Card>

          {/* Data Security */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="h-5 w-5 text-purple-600" />
                3. Data Security
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-muted-foreground">
                We implement industry-standard security measures to protect your data:
              </p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li>256-bit SSL encryption for all data transmission</li>
                <li>Secure data storage on servers located in India</li>
                <li>Regular security audits and vulnerability assessments</li>
                <li>Access controls and authentication mechanisms</li>
                <li>Employee training on data protection</li>
              </ul>
            </CardContent>
          </Card>

          {/* Data Sharing */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5 text-amber-600" />
                4. Data Sharing
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-muted-foreground">
                We may share your information with:
              </p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li><strong>Other Users:</strong> Business information visible to logged-in users for trade purposes</li>
                <li><strong>Service Providers:</strong> Third-party services that help us operate the platform</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
              </ul>
              <p className="text-sm bg-green-50 p-3 rounded-lg border border-green-200 mt-3">
                <strong>We do not sell your personal information to third parties.</strong>
              </p>
            </CardContent>
          </Card>

          {/* Your Rights */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-blue-600" />
                5. Your Rights
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-3">You have the right to:</p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li>Access and download your personal data</li>
                <li>Correct inaccurate information</li>
                <li>Delete your account and associated data</li>
                <li>Opt-out of marketing communications</li>
                <li>Control phone number visibility to other users</li>
                <li>Request data portability</li>
              </ul>
            </CardContent>
          </Card>

          {/* Contact */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-purple-600" />
                6. Contact Us
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-3">
                If you have questions about this Privacy Policy or your data, contact us at:
              </p>
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="font-semibold">GlobalVyapar</p>
                <p className="text-muted-foreground">Email: privacy@globalvyapar.co.in</p>
                <p className="text-muted-foreground">Website: www.globalvyapar.co.in</p>
              </div>
            </CardContent>
          </Card>
        </div>

        <Separator className="my-8" />
        
        <p className="text-center text-sm text-muted-foreground">
          By using GlobalVyapar, you agree to this Privacy Policy.
        </p>
      </div>
    </Layout>
  );
}
